/**
 * Blockchain Service - Hidden Backend Layer
 *
 * This service handles all blockchain interactions.
 * IMPORTANT: This is NEVER exposed directly to the UI.
 * All blockchain operations are abstracted through evidenceService.js
 *
 * Core Principle: "The notary that never sleeps" - invisible infrastructure
 */

import { ethers } from 'ethers';

class BlockchainService {
  constructor() {
    this.provider = null;
    this.contract = null;
    this.initialized = false;
    this.backendMode = true; // In production, this would be server-side

    // Contract configuration (from environment)
    this.config = {
      rpcUrl: process.env.REACT_APP_BASE_RPC_URL || 'https://base-mainnet.g.alchemy.com/v2/demo',
      chainId: parseInt(process.env.REACT_APP_CHAIN_ID || '8453'),
      contractAddress: process.env.REACT_APP_CONTRACT_ADDRESS || '0x4B2cF77Dbd063299536AB916Fb1445bD3267162F'
    };
  }

  /**
   * Initialize blockchain connection
   * @private
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Connect to blockchain (read-only for verification)
      this.provider = new ethers.JsonRpcProvider(this.config.rpcUrl);

      // In production, write operations would be server-side
      // with proper key management
      console.log('[BlockchainService] Connected to Base network');

      this.initialized = true;
    } catch (error) {
      console.error('[BlockchainService] Initialization failed:', error);
      throw new Error('Unable to connect to verification network');
    }
  }

  /**
   * Create evidence record on blockchain
   *
   * In production: This would be a server-side API call
   * Server would handle wallet, gas, and blockchain submission
   *
   * @param {Object} evidenceData - Evidence metadata
   * @returns {Promise<Object>} Blockchain record
   */
  async createEvidenceRecord(evidenceData) {
    await this.initialize();

    try {
      // Generate unique verification ID
      const verificationId = this.generateVerificationId(evidenceData);

      // Generate short verification code (user-friendly)
      const verificationCode = this.generateVerificationCode(verificationId);

      // In production: Send to server API
      // const response = await fetch('/api/evidence/create', {
      //   method: 'POST',
      //   body: JSON.stringify(evidenceData)
      // });

      // For now: Simulate blockchain transaction
      const record = {
        id: verificationId,
        code: verificationCode,
        fingerprint: evidenceData.fingerprint,
        ipfsHash: evidenceData.ipfsHash,
        fileName: evidenceData.fileName,
        fileSize: evidenceData.fileSize,
        fileType: evidenceData.fileType,
        timestamp: evidenceData.timestamp,
        blockNumber: await this.provider.getBlockNumber(),
        transactionHash: '0x' + this.generateHash(verificationId),
        status: 'VERIFIED',
        metadata: evidenceData
      };

      // Store in local cache (in production: server database)
      this.cacheRecord(record);

      console.log('[BlockchainService] Evidence record created:', verificationCode);

      return record;

    } catch (error) {
      console.error('[BlockchainService] Create record failed:', error);
      throw new Error('Unable to create verification record');
    }
  }

  /**
   * Get evidence record from blockchain
   *
   * @param {string} verificationCodeOrId - Verification code or ID
   * @returns {Promise<Object>} Evidence record
   */
  async getEvidenceRecord(verificationCodeOrId) {
    await this.initialize();

    try {
      // Try to get from cache first
      const cached = this.getCachedRecord(verificationCodeOrId);
      if (cached) return cached;

      // In production: Query server API
      // const response = await fetch(`/api/evidence/verify/${verificationCodeOrId}`);
      // return await response.json();

      // For now: Return null (not found)
      return null;

    } catch (error) {
      console.error('[BlockchainService] Get record failed:', error);
      throw new Error('Unable to retrieve verification record');
    }
  }

  /**
   * Record custody transfer on blockchain
   *
   * @param {Object} transferData - Custody transfer details
   * @returns {Promise<Object>} Transfer record
   */
  async recordCustodyTransfer(transferData) {
    await this.initialize();

    try {
      const transferId = this.generateHash(JSON.stringify(transferData));

      const transfer = {
        id: transferId,
        verificationId: transferData.verificationId,
        fromParty: transferData.fromParty,
        toParty: transferData.toParty,
        timestamp: transferData.timestamp,
        notes: transferData.notes,
        blockNumber: await this.provider.getBlockNumber(),
        transactionHash: '0x' + this.generateHash(transferId)
      };

      // Cache transfer
      this.cacheCustodyTransfer(transfer);

      console.log('[BlockchainService] Custody transfer recorded:', transferId);

      return transfer;

    } catch (error) {
      console.error('[BlockchainService] Record custody transfer failed:', error);
      throw new Error('Unable to record custody transfer');
    }
  }

  /**
   * Get custody chain for evidence
   *
   * @param {string} verificationId - Evidence verification ID
   * @returns {Promise<Array>} Chain of custody transfers
   */
  async getCustodyChain(verificationId) {
    await this.initialize();

    try {
      // Get from cache (in production: query server)
      const chain = this.getCachedCustodyChain(verificationId);

      return chain || [];

    } catch (error) {
      console.error('[BlockchainService] Get custody chain failed:', error);
      throw new Error('Unable to retrieve custody chain');
    }
  }

  /**
   * Get statistics (for dashboard)
   */
  async getStatistics() {
    try {
      // In production: Query server API for aggregated stats
      const stats = {
        totalRecords: this.getCacheSize(),
        recordsToday: this.getTodayRecords(),
        averageBlockTime: '2 seconds',
        networkStatus: 'OPERATIONAL'
      };

      return stats;

    } catch (error) {
      console.error('[BlockchainService] Get statistics failed:', error);
      return {
        totalRecords: 0,
        recordsToday: 0,
        averageBlockTime: 'N/A',
        networkStatus: 'UNKNOWN'
      };
    }
  }

  /**
   * Generate unique verification ID
   * @private
   */
  generateVerificationId(data) {
    const hash = this.generateHash(JSON.stringify(data) + Date.now());
    return 'EVD-' + hash.substring(0, 16).toUpperCase();
  }

  /**
   * Generate short verification code (user-friendly)
   * @private
   */
  generateVerificationCode(verificationId) {
    const hash = this.generateHash(verificationId);
    // Format: ABC-DEF-GHI (easier to type/read)
    return [
      hash.substring(0, 3),
      hash.substring(3, 6),
      hash.substring(6, 9)
    ].join('-').toUpperCase();
  }

  /**
   * Generate hash
   * @private
   */
  generateHash(input) {
    // Simple hash for demo (in production: use proper crypto)
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Cache management (in production: server-side database)
   * @private
   */
  cacheRecord(record) {
    try {
      const cache = this.getCache();
      cache.records = cache.records || {};
      cache.records[record.id] = record;
      cache.records[record.code] = record; // Also index by code
      localStorage.setItem('evidencer_cache', JSON.stringify(cache));
    } catch (error) {
      console.error('[BlockchainService] Cache write failed:', error);
    }
  }

  getCachedRecord(idOrCode) {
    try {
      const cache = this.getCache();
      return cache.records?.[idOrCode] || null;
    } catch (error) {
      console.error('[BlockchainService] Cache read failed:', error);
      return null;
    }
  }

  cacheCustodyTransfer(transfer) {
    try {
      const cache = this.getCache();
      cache.custody = cache.custody || {};
      cache.custody[transfer.verificationId] = cache.custody[transfer.verificationId] || [];
      cache.custody[transfer.verificationId].push(transfer);
      localStorage.setItem('evidencer_cache', JSON.stringify(cache));
    } catch (error) {
      console.error('[BlockchainService] Cache custody write failed:', error);
    }
  }

  getCachedCustodyChain(verificationId) {
    try {
      const cache = this.getCache();
      return cache.custody?.[verificationId] || [];
    } catch (error) {
      console.error('[BlockchainService] Cache custody read failed:', error);
      return [];
    }
  }

  getCache() {
    try {
      const cached = localStorage.getItem('evidencer_cache');
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      return {};
    }
  }

  getCacheSize() {
    const cache = this.getCache();
    return Object.keys(cache.records || {}).length / 2; // Divide by 2 (indexed by both ID and code)
  }

  getTodayRecords() {
    const cache = this.getCache();
    const today = new Date().toDateString();
    let count = 0;

    Object.values(cache.records || {}).forEach(record => {
      if (record.id && record.id.startsWith('EVD-')) { // Only count IDs, not codes
        const recordDate = new Date(record.timestamp).toDateString();
        if (recordDate === today) count++;
      }
    });

    return count;
  }
}

// Export singleton instance
const blockchainService = new BlockchainService();

export default blockchainService;
export { BlockchainService };
